import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { safeSessionStorage } from '@/utils/storage';

/**
 * useCityPricing — Million-User Grade Unified Pricing Hook
 *
 * PROBLEM SOLVED:
 * Previously, ServiceDetail.tsx, SubServiceDetail.tsx, and Index.tsx each had
 * their own raw useEffect that fetched city pricing on every mount, causing:
 *   1. Skeleton UI to flash on every navigation (even when service data was cached)
 *   2. Triplicated pricing logic that was a maintenance nightmare
 *   3. Inconsistent pricing if one copy was updated but not the others
 *
 * HOW BIG COMPANIES DO IT (Zomato, Urban Company):
 *   - First visit: Fetch pricing, cache in memory for 1 hour
 *   - Every subsequent visit: Instant load from React Query memory cache
 *   - Background revalidation after staleTime expires — user never sees loading
 *
 * USAGE:
 *   const { getCityPrice, cityDiscount, isReady } = useCityPricing();
 *   const { price, mrp, discount } = getCityPrice(subServiceId, subService);
 */

interface CityPriceEntry {
  price: number;
  mrpMarkupPercentage?: number;
  mrpOverride?: number;
}

interface SubServiceInput {
  price: number;
  mrpMarkupPercentage?: number;
  mrpOverride?: number;
}

interface PricingResult {
  price: number;
  mrp: number | null;
  discount: number | null;
}

// Fetch pricing settings (city_pricing_enabled, default_pricing_city_id)
const fetchPricingSettings = async (): Promise<Record<string, string>> => {
  const { data } = await supabase
    .from('app_settings')
    .select('key, value')
    .in('key', ['city_pricing_enabled', 'default_pricing_city_id']);
  const settings: Record<string, string> = {};
  data?.forEach((s: { key: string; value: string }) => {
    settings[s.key] = s.value;
  });
  return settings;
};

// Fetch all city prices for the detected/default city
const fetchCityPrices = async (
  cityId: string
): Promise<Record<string, CityPriceEntry>> => {
  const { data } = await (supabase.from('sub_service_pricing' as any) as any)
    .select('sub_service_id, price, mrp_markup_percentage, mrp_override')
    .eq('city_id', cityId);

  const map: Record<string, CityPriceEntry> = {};
  data?.forEach((d: any) => {
    map[d.sub_service_id] = {
      price: Number(d.price),
      mrpMarkupPercentage: d.mrp_markup_percentage
        ? Number(d.mrp_markup_percentage)
        : undefined,
      mrpOverride: d.mrp_override ? Number(d.mrp_override) : undefined,
    };
  });
  return map;
};

// Fetch city-level marketing discount
const fetchCityDiscount = async (
  cityId: string
): Promise<number | undefined> => {
  const { data } = await supabase
    .from('cities')
    .select('marketing_discount_percent')
    .eq('id', cityId)
    .maybeSingle();

  if (
    data?.marketing_discount_percent &&
    Number(data.marketing_discount_percent) > 0
  ) {
    return Number(data.marketing_discount_percent);
  }
  return undefined;
};

/**
 * Resolves the active city ID from session storage or default setting.
 */
const getActiveCityId = (
  settings: Record<string, string>
): string | null => {
  if (settings['city_pricing_enabled'] !== 'true') return null;
  const detected = safeSessionStorage.getItem('detected_city_id');
  return detected || settings['default_pricing_city_id'] || null;
};

/**
 * Resolves the MRP cascade for a sub-service.
 * Priority: cityData.mrpMarkup > cityDiscount > sub.mrpMarkup > undefined
 * Extracted to avoid code duplication and reduce cognitive complexity.
 */
const resolveMrpCascade = (
  sub: SubServiceInput,
  cityData: CityPriceEntry | undefined,
  cityDiscount: number | undefined
): { effectivePct: number | undefined; effectiveOverride: number | undefined } => {
  if (!cityData) {
    // No city-specific price override
    const pct = (cityDiscount && cityDiscount > 0 && !sub.mrpOverride)
      ? cityDiscount
      : (sub.mrpMarkupPercentage || cityDiscount);
    return { effectivePct: pct, effectiveOverride: sub.mrpOverride || undefined };
  }

  // City data exists — apply cascade
  let effectivePct: number | undefined;
  if (cityData.mrpMarkupPercentage && cityData.mrpMarkupPercentage > 0) {
    effectivePct = cityData.mrpMarkupPercentage;
  } else if (cityDiscount && cityDiscount > 0) {
    effectivePct = cityDiscount;
  } else if (sub.mrpMarkupPercentage && sub.mrpMarkupPercentage > 0) {
    effectivePct = sub.mrpMarkupPercentage;
  }

  const effectiveOverride = (cityData.mrpOverride && cityData.mrpOverride > 0)
    ? cityData.mrpOverride
    : (sub.mrpOverride || undefined);

  return { effectivePct, effectiveOverride };
};

export const useCityPricing = () => {
  // Step 1: Fetch pricing settings (cached 1 hour)
  const { data: pricingSettings = {} } = useQuery({
    queryKey: ['pricingSettings'],
    queryFn: fetchPricingSettings,
    staleTime: 60 * 60 * 1000, // 1 hour — instant on revisit
    gcTime: 24 * 60 * 60 * 1000, // Keep in memory 24 hours
    refetchOnWindowFocus: false,
  });

  const activeCityId = useMemo(
    () => getActiveCityId(pricingSettings),
    [pricingSettings]
  );

  // Step 2: Fetch city prices (cached 1 hour)
  const { data: cityPrices = {} } = useQuery({
    queryKey: ['cityPrices', activeCityId],
    queryFn: () => (activeCityId ? fetchCityPrices(activeCityId) : Promise.resolve({})),
    enabled: !!activeCityId,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Step 3: Fetch city discount (cached 1 hour)
  const { data: cityDiscount } = useQuery({
    queryKey: ['cityDiscount', activeCityId],
    queryFn: () => (activeCityId ? fetchCityDiscount(activeCityId) : Promise.resolve(undefined)),
    enabled: !!activeCityId,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  /**
   * Get the final resolved price for a sub-service.
   * Applies the same cascade logic that was previously duplicated across 3 files:
   *   1. City-specific price override (from sub_service_pricing table)
   *   2. City-specific MRP markup (sub_service_pricing.mrp_markup_percentage)
   *   3. City-level marketing discount (cities.marketing_discount_percent)
   *   4. Global sub-service MRP markup (sub_services.mrp_markup_percentage)
   *   5. Global sub-service MRP override (sub_services.mrp_override)
   */
  const getCityPrice = useMemo(() => {
    return (subServiceId: string, sub: SubServiceInput): PricingResult => {
      const cityData = cityPrices[subServiceId];
      const price = cityData ? cityData.price : sub.price;
      const { effectivePct, effectiveOverride } = resolveMrpCascade(sub, cityData, cityDiscount);

      const mrp =
        effectiveOverride ||
        (effectivePct && effectivePct > 0 && effectivePct < 100
          ? Math.round(price / (1 - effectivePct / 100))
          : null);

      const discount =
        mrp && mrp > price
          ? Math.round(((mrp - price) / mrp) * 100)
          : null;

      return { price, mrp, discount };
    };
  }, [cityPrices, cityDiscount]);

  /**
   * Enrich an entire sub-service with city pricing data.
   * Used by Index.tsx to transform the service list.
   */
  const enrichSubService = useMemo(() => {
    return <T extends SubServiceInput & { id: string }>(sub: T): T & { mrpMarkupPercentage?: number; mrpOverride?: number } => {
      const cityData = cityPrices[sub.id];
      const { effectivePct, effectiveOverride } = resolveMrpCascade(sub, cityData, cityDiscount);

      if (cityData) {
        return {
          ...sub,
          price: cityData.price,
          mrpMarkupPercentage: effectivePct,
          mrpOverride: effectiveOverride,
        };
      }

      // No city price override — apply cascade result
      if (effectivePct !== sub.mrpMarkupPercentage || effectiveOverride !== sub.mrpOverride) {
        return { ...sub, mrpMarkupPercentage: effectivePct, mrpOverride: effectiveOverride };
      }

      return sub; // No city pricing → use global as-is
    };
  }, [cityPrices, cityDiscount]);

  return {
    /** Get resolved price for a single sub-service */
    getCityPrice,
    /** Enrich a sub-service object with city pricing data */
    enrichSubService,
    /** Raw city prices map (for advanced use) */
    cityPrices,
    /** City-level marketing discount percentage */
    cityDiscount,
    /** Whether pricing data is ready (all queries resolved) */
    isReady: Object.keys(pricingSettings).length > 0,
  };
};
